import { useCallback } from "react";
import { useNotifications } from "~/contexts/useNotifications";
import { performRoll } from "~/utils/rollUtils";
import { generateUUID } from "~/utils/uuid";
import type { RollResult } from "~/types/character";

/**
 * Hook for dice rolling with automatic notifications
 *
 * Shared by CharacterSheet and GmView. Automatically adds roll results
 * to the notification system.
 *
 * @example
 * ```tsx
 * const { rollDice } = useRolling();
 * 
 * // In CharacterSheet
 * rollDice("Mind Check", 3);
 * 
 * // In GmView
 * rollDice("Mind Check", 3, 0, "Carmine Rose");
 * ```
 */
export function useRolling() {
  const { addRollNotification } = useNotifications();

  const rollDice = useCallback(
    (
      skillName: string,
      poolSize: number,
      flatHits = 0,
      characterName?: string
    ): RollResult => {
      const rollResult = performRoll(poolSize, flatHits);
      const displayName = characterName
        ? `${characterName}: ${skillName}`
        : skillName;

      const newRoll: RollResult = {
        id: generateUUID(),
        name: displayName,
        hits: rollResult.hits,
        flatHits,
        rolls: rollResult.rolls,
        isFumble: rollResult.isFumble,
        isCritFumble: rollResult.isCritFumble,
      };

      addRollNotification(newRoll);
      return newRoll;
    },
    [addRollNotification]
  );

  return { rollDice };
}
